import { useState, useMemo } from "react";
import {
  Accordion,
  Stack,
  TextInput,
  Box,
  Text,
  ActionIcon,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconSearch, IconX } from "@tabler/icons-react";
import { L2KH2 } from "../Typography";
import { L2KFAQAccordion } from "./L2KFAQAccordion";
import { faqData, FAQItem } from "../../../data/faq-data";
import "./faq.css";

/**
 * L2KFAQSection - Main FAQ container component with search and filtering
 *
 * Features:
 * - Live search functionality
 * - Flexible filtering (by page, section, tags, IDs)
 * - Fully accessible (WCAG compliant)
 * - Mobile responsive
 * - Customizable display options
 *
 * @example
 * <L2KFAQSection
 *   title="Frequently Asked Questions"
 *   subtitle="Everything you need to know"
 *   showSearch={true}
 *   allowMultiple={true}
 * />
 */
export interface L2KFAQSectionProps {
  /** Main title for the FAQ section */
  title?: string;
  /** Subtitle/description text */
  subtitle?: string;
  /** Whether to show the search input */
  showSearch?: boolean;
  /** Placeholder text for search input */
  searchPlaceholder?: string;
  /** Whether multiple items can be expanded at once */
  allowMultiple?: boolean;
  /** IDs of items to expand by default */
  defaultExpanded?: string[];
  /** Filter FAQs by specific section */
  section?: string;
  /** Filter FAQs by tags */
  tags?: string[];
  /** Filter FAQs by specific IDs */
  ids?: string[];
  /** Filter FAQs by page (showOnPages) */
  page?: string;
  /** Limit the number of FAQs displayed */
  limit?: number;
  /** Highlight expanded items */
  highlightExpanded?: boolean;
  /** Custom CSS class */
  className?: string;
}

export function L2KFAQSection({
  title = "Frequently Asked Questions",
  subtitle,
  showSearch = true,
  searchPlaceholder = "Search FAQs...",
  allowMultiple = true,
  defaultExpanded = [],
  section,
  tags,
  ids,
  page,
  limit,
  highlightExpanded = true,
  className,
}: L2KFAQSectionProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Filter FAQs based on props
  const filteredFAQs = useMemo(() => {
    let filtered: FAQItem[] = [...faqData];

    // Filter by section
    if (section) {
      filtered = filtered.filter((faq) => faq.section === section);
    }

    // Filter by tags
    if (tags && tags.length > 0) {
      filtered = filtered.filter((faq) =>
        tags.some((tag) => faq.tags.includes(tag))
      );
    }

    // Filter by IDs
    if (ids && ids.length > 0) {
      filtered = filtered.filter((faq) => ids.includes(faq.id));
    }

    // Filter by page
    if (page) {
      filtered = filtered.filter(
        (faq) => !faq.showOnPages || faq.showOnPages.includes(page)
      );
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (faq) =>
          faq.question.toLowerCase().includes(query) ||
          faq.section.toLowerCase().includes(query) ||
          faq.tags.some((tag) => tag.toLowerCase().includes(query)) ||
          // Search in answer text (basic string matching)
          (typeof faq.answer === "string" &&
            faq.answer.toLowerCase().includes(query))
      );
    }

    // Sort by priority
    filtered.sort((a, b) => (a.priority || 999) - (b.priority || 999));

    // Apply limit
    if (limit && limit > 0) {
      filtered = filtered.slice(0, limit);
    }

    return filtered;
  }, [section, tags, ids, page, searchQuery, limit]);

  // Get default expanded value
  const defaultValue = useMemo(() => {
    if (allowMultiple) {
      return defaultExpanded;
    }
    return defaultExpanded.length > 0 ? defaultExpanded[0] : null;
  }, [allowMultiple, defaultExpanded]);

  return (
    <Box className={className}>
      <Stack gap="lg">
        {/* Title and Subtitle */}
        {(title || subtitle) && (
          <Box>
            {title && (
              <L2KH2 variant="primary" className="l2k-faq-section-title">
                {title}
              </L2KH2>
            )}
            {subtitle && (
              <Text
                size="sm"
                c="dimmed"
                className="l2k-faq-section-subtitle"
                mt="xs"
              >
                {subtitle}
              </Text>
            )}
          </Box>
        )}

        {/* Search Input */}
        {showSearch && (
          <TextInput
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.currentTarget.value)}
            leftSection={<IconSearch size={16} color="#FF9800" />}
            rightSection={
              searchQuery ? (
                <ActionIcon
                  size="xs"
                  variant="subtle"
                  color="gray"
                  onClick={() => setSearchQuery("")}
                  aria-label="Clear search"
                >
                  <IconX size={14} />
                </ActionIcon>
              ) : null
            }
            className="l2k-faq-search-input"
            size={isMobile ? "md" : "md"}
          />
        )}

        {/* FAQ Accordion */}
        {filteredFAQs.length > 0 ? (
          <Accordion
            multiple={allowMultiple}
            defaultValue={defaultValue}
            variant="separated"
            radius="md"
          >
            {filteredFAQs.map((faq) => (
              <L2KFAQAccordion
                key={faq.id}
                value={faq.id}
                question={faq.question}
                answer={faq.answer}
              />
            ))}
          </Accordion>
        ) : (
          <Box className="l2k-faq-no-results">
            <Text size="md" c="dimmed">
              No FAQs found. Try adjusting your search or filters.
            </Text>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
